import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { User, Building2, Repeat, Zap, ArrowLeft, ArrowRight, Pencil, AlertTriangle, CheckCircle2, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, URGENCY, MODALITY } from "@/lib/api";
import { loadDraft, saveDraft, clearDraft, getUtm, setNext, useSeo } from "@/lib/draft";
import { useAuth, useSettings } from "@/context/AppContext";
import { AuthPanel, UfSelect, maskPhone, phoneOk } from "@/components/AuthPanel";
import { ShareBlock } from "@/components/ShareBlock";

const EMPTY = { requester_type: null, modality: null, category_slug: null, services: [], description: "", answers: {}, urgency: null, deadline_date: "", step: 0,
  company: { name: "", cnpj: "", sector: "", state: "", employees: "", needs: "", frequency: "" } };
const STEP_NAMES = ["Perfil", "Área", "Descrição", "Urgência", "Revisão"];
const maskCnpj = (v) => v.replace(/\D/g, "").slice(0, 14).replace(/^(\d{2})(\d)/, "$1.$2").replace(/^(\d{2})\.(\d{3})(\d)/, "$1.$2.$3").replace(/\.(\d{3})(\d)/, ".$1/$2").replace(/(\d{4})(\d)/, "$1-$2");

const Choice = ({ selected, onClick, icon: I, title, text, testid }) => (
  <button type="button" className="select-card flex gap-4" data-selected={selected} onClick={onClick} data-testid={testid}>
    {I && <span className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl ${selected ? "bg-[#1465C8] text-white" : "bg-[#EDF5FF] text-[#1465C8]"}`}><I size={22} /></span>}
    <span><span className="block font-bold">{title}</span>{text && <span className="mt-0.5 block text-sm text-[#64748B]">{text}</span>}</span>
  </button>
);

function StepPerfil({ d, set }) {
  return (
    <div className="grid gap-3 md:grid-cols-2">
      <Choice icon={User} selected={d.requester_type === "pf"} title="Pessoa Física" text="Preciso de orientação sobre uma situação pessoal." testid="wizard-type-pf"
        onClick={() => set({ requester_type: "pf", modality: "pf", services: [], step: 1 })} />
      <Choice icon={Building2} selected={d.requester_type === "pj"} title="Empresa" text="Busco orientação jurídica para minha empresa." testid="wizard-type-pj"
        onClick={() => set({ requester_type: "pj", modality: d.modality === "pf" ? null : d.modality, category_slug: null, step: 1 })} />
    </div>
  );
}

function StepArea({ d, set, cats, bs }) {
  if (d.requester_type === "pf")
    return (
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3" data-testid="wizard-category-grid">
        {cats.map((c) => (
          <Choice key={c.slug} selected={d.category_slug === c.slug} title={c.name} text={c.description} testid={`area-card-${c.slug}`} onClick={() => set({ category_slug: c.slug, answers: {} })} />
        ))}
      </div>
    );
  const list = d.modality ? bs[d.modality] || [] : [];
  const toggle = (s) => set({ services: d.services.includes(s) ? d.services.filter((x) => x !== s) : [...d.services, s] });
  return (
    <div className="space-y-6">
      <div className="grid gap-3 md:grid-cols-2">
        <Choice icon={Repeat} selected={d.modality === "recorrente"} title="Consultoria jurídica recorrente" text="Assessoria jurídica mensal, com proposta individualizada." testid="wizard-modality-recorrente" onClick={() => set({ modality: "recorrente", services: [] })} />
        <Choice icon={Zap} selected={d.modality === "pontual"} title="Demanda jurídica pontual" text="Um serviço específico para a sua empresa." testid="wizard-modality-pontual" onClick={() => set({ modality: "pontual", services: [] })} />
      </div>
      {d.modality && (
        <div>
          <p className="label">{d.modality === "recorrente" ? "Áreas de interesse" : "Serviço desejado"} (selecione um ou mais)</p>
          <div className="flex flex-wrap gap-2" data-testid="wizard-services">
            {list.map((s, i) => (
              <button key={s} type="button" onClick={() => toggle(s)} data-testid={`service-chip-${i}`}
                className={`rounded-full border px-4 py-2 text-sm font-semibold transition-colors ${d.services.includes(s) ? "border-[#1465C8] bg-[#1465C8] text-white" : "border-[#3198EF]/30 bg-white hover:bg-[#EDF5FF]"}`}>{s}</button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function StepDescricao({ d, set, cat }) {
  const c = d.company;
  const setC = (k, v) => set({ company: { ...c, [k]: v } });
  return (
    <div className="space-y-5">
      {d.requester_type === "pj" && (
        <div className="grid gap-4 rounded-2xl bg-[#EDF5FF] p-4 sm:grid-cols-2 md:p-5" data-testid="wizard-company-fields">
          <div className="sm:col-span-2"><label className="label">Nome da empresa *</label><input className="field" value={c.name} onChange={(e) => setC("name", e.target.value)} data-testid="company-name-input" /></div>
          <div><label className="label">CNPJ (se disponível)</label><input className="field" inputMode="numeric" value={c.cnpj} onChange={(e) => setC("cnpj", maskCnpj(e.target.value))} data-testid="company-cnpj-input" /></div>
          <div><label className="label">Ramo de atividade</label><input className="field" value={c.sector} onChange={(e) => setC("sector", e.target.value)} data-testid="company-sector-input" /></div>
          <div><label className="label">Estado de atuação</label><UfSelect value={c.state} onChange={(v) => setC("state", v)} testid="company-state-select" /></div>
          <div><label className="label">Empregados (aprox.)</label>
            <select className="field" value={c.employees} onChange={(e) => setC("employees", e.target.value)} data-testid="company-employees-select">
              <option value="">Selecione</option>{["1-9", "10-49", "50-99", "100-499", "500+"].map((x) => <option key={x}>{x}</option>)}
            </select></div>
          {d.modality === "recorrente" && (
            <>
              <div><label className="label">Frequência estimada de suporte</label>
                <select className="field" value={c.frequency} onChange={(e) => setC("frequency", e.target.value)} data-testid="company-frequency-select">
                  <option value="">Selecione</option>{["Eventual", "Semanal", "Diária", "Não sei estimar"].map((x) => <option key={x}>{x}</option>)}
                </select></div>
              <div className="sm:col-span-2"><label className="label">Principais necessidades jurídicas</label><input className="field" value={c.needs} onChange={(e) => setC("needs", e.target.value)} data-testid="company-needs-input" /></div>
            </>
          )}
        </div>
      )}
      <div>
        <label className="label text-base">{d.requester_type === "pj" ? "Descreva as dificuldades enfrentadas e a orientação que procura." : "Conte o que aconteceu e qual orientação você procura."}</label>
        <p className="mb-2 text-sm text-[#64748B]">Não é necessário utilizar termos jurídicos.</p>
        <textarea className="field min-h-[200px] resize-y py-3 leading-relaxed" maxLength={8000} value={d.description} onChange={(e) => set({ description: e.target.value })} data-testid="wizard-description-textarea" placeholder="Escreva com suas palavras..." />
        <p className="mt-1 text-right text-xs text-[#64748B]">{d.description.length < 20 ? `Mínimo de 20 caracteres (${d.description.length})` : `${d.description.length}/8000`}</p>
      </div>
      {cat?.questions?.length > 0 && (
        <div className="space-y-4">
          <p className="text-sm font-bold text-[#1465C8]">Perguntas complementares (opcionais)</p>
          {cat.questions.map((q, i) => (
            <div key={q}><label className="label">{q}</label><input className="field" value={d.answers[q] || ""} onChange={(e) => set({ answers: { ...d.answers, [q]: e.target.value } })} data-testid={`wizard-extra-question-${i}`} /></div>
          ))}
        </div>
      )}
    </div>
  );
}

function StepUrgencia({ d, set }) {
  return (
    <div className="space-y-5">
      <div className="grid gap-3 md:grid-cols-3">
        {[["normal", "Sem prazo próximo."], ["prioritaria", "Precisa de atenção em breve."], ["urgente", "Há prazo, audiência ou fato muito próximo."]].map(([k, t]) => (
          <Choice key={k} selected={d.urgency === k} title={URGENCY[k]} text={t} testid={`urgency-${k}`} onClick={() => set({ urgency: k })} />
        ))}
      </div>
      <div className="max-w-xs"><label className="label">Há prazo, audiência ou data importante? (opcional)</label>
        <input type="date" className="field" value={d.deadline_date} onChange={(e) => set({ deadline_date: e.target.value })} data-testid="wizard-deadline-input" /></div>
      <p className="flex gap-2 rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900" data-testid="urgency-warning">
        <AlertTriangle size={18} className="shrink-0" /> O envio não garante atendimento imediato e não suspende prazos legais. Se houver prazo correndo, procure orientação profissional sem demora.
      </p>
    </div>
  );
}

const Row = ({ k, v, onEdit, testid }) => (
  <div className="flex items-start justify-between gap-4 border-b border-slate-100 py-3">
    <div><p className="text-xs font-semibold uppercase tracking-wider text-[#64748B]">{k}</p><p className="mt-0.5 whitespace-pre-wrap break-words font-medium">{v || "—"}</p></div>
    {onEdit && <button type="button" className="btn-ghost shrink-0" onClick={onEdit} data-testid={testid}><Pencil size={14} />Editar</button>}
  </div>
);

function CompleteProfile({ user, onDone }) {
  const { setUser } = useAuth();
  const [f, setF] = useState({ name: user.name || "", state: user.state || "", phone: user.phone || "" });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    if (!f.state || !phoneOk(f.phone)) return toast.error("Informe estado e celular com DDD.");
    setBusy(true);
    try {
      const { data } = await api.put("/me/profile", f);
      setUser({ ...user, ...data });
      onDone?.();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <div className="space-y-4" data-testid="complete-profile-form">
      <p className="text-sm text-[#64748B]">Complete seu cadastro para enviar.</p>
      <input className="field" placeholder="Nome completo" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="complete-name-input" />
      <div className="grid gap-4 sm:grid-cols-2">
        <UfSelect value={f.state} onChange={(v) => setF({ ...f, state: v })} testid="complete-state-select" />
        <input className="field" placeholder="Celular com DDD" value={f.phone} onChange={(e) => setF({ ...f, phone: maskPhone(e.target.value) })} data-testid="complete-phone-input" />
      </div>
      <button className="btn-primary w-full" onClick={save} disabled={busy} data-testid="complete-profile-save-btn">Salvar dados</button>
    </div>
  );
}

function StepRevisao({ d, set, cat, onSubmit, sending }) {
  const { user } = useAuth();
  const [consent, setConsent] = useState(false);
  const ready = user && user.role === "user" && user.phone && user.state;
  return (
    <div className="grid gap-6 lg:grid-cols-[1.2fr_1fr]">
      <div className="card" data-testid="review-summary">
        <h3 className="mb-2 text-lg font-extrabold">Resumo da solicitação</h3>
        <Row k="Perfil" v={MODALITY[d.modality] ? `${d.requester_type === "pf" ? "Pessoa Física" : "Empresa"}${d.requester_type === "pj" ? " — " + MODALITY[d.modality] : ""}` : ""} onEdit={() => set({ step: 0 })} testid="review-edit-type" />
        <Row k={d.requester_type === "pf" ? "Área" : "Serviços"} v={d.requester_type === "pf" ? cat?.name : d.services.join(", ")} onEdit={() => set({ step: 1 })} testid="review-edit-area" />
        {d.requester_type === "pj" && <Row k="Empresa" v={[d.company.name, d.company.cnpj, d.company.sector, d.company.state, d.company.employees && `${d.company.employees} empregados`].filter(Boolean).join(" · ")} onEdit={() => set({ step: 2 })} testid="review-edit-company" />}
        <Row k="Descrição" v={d.description} onEdit={() => set({ step: 2 })} testid="review-edit-description" />
        {Object.entries(d.answers).filter(([, v]) => v).map(([q, v]) => <Row key={q} k={q} v={v} />)}
        <Row k="Urgência" v={`${URGENCY[d.urgency] || ""}${d.deadline_date ? ` · data informada: ${new Date(d.deadline_date + "T12:00").toLocaleDateString("pt-BR")}` : ""}`} onEdit={() => set({ step: 3 })} testid="review-edit-urgency" />
      </div>
      <div className="card h-fit" data-testid="review-auth-area">
        {!user || user.role !== "user" ? (
          <>
            <h3 className="text-lg font-extrabold">Último passo: identifique-se</h3>
            <p className="mb-5 mt-1 text-sm text-[#64748B]">Suas respostas estão salvas e não serão perdidas.</p>
            <AuthPanel returnPath="/solicitar" />
          </>
        ) : !ready ? (
          <CompleteProfile user={user} />
        ) : (
          <>
            <h3 className="text-lg font-extrabold">Confirmar envio</h3>
            <p className="mt-1 text-sm text-[#64748B]">Enviando como <strong>{user.name}</strong> ({user.email})</p>
            <label className="mt-5 flex gap-3 text-sm">
              <input type="checkbox" className="mt-1 h-5 w-5 accent-[#1465C8]" checked={consent} onChange={(e) => setConsent(e.target.checked)} data-testid="review-consent-checkbox" />
              <span>Li e aceito os <Link to="/termos" className="font-semibold text-[#1465C8] underline">Termos de Uso</Link> e a <Link to="/privacidade" className="font-semibold text-[#1465C8] underline">Política de Privacidade</Link>. Entendo que o envio não implica contratação.</span>
            </label>
            <button className="btn-primary mt-5 w-full text-lg" disabled={!consent || sending} onClick={onSubmit} data-testid="submit-request-button">{sending ? "Enviando..." : "Enviar solicitação"}</button>
          </>
        )}
      </div>
    </div>
  );
}

const canAdvance = (d) => [
  !!d.requester_type,
  d.requester_type === "pf" ? !!d.category_slug : !!d.modality && d.services.length > 0,
  d.description.trim().length >= 20 && (d.requester_type === "pf" || d.company.name.trim().length >= 2),
  !!d.urgency,
][d.step];

export default function Solicitar() {
  useSeo("Nova solicitação", null, true);
  const [params] = useSearchParams();
  const nav = useNavigate();
  const { settings } = useSettings() || {};
  const [cats, setCats] = useState([]);
  const [d, setD] = useState(() => {
    const saved = loadDraft();
    const tipo = params.get("tipo");
    if (saved && (!tipo || saved.requester_type === tipo) && !params.get("area") && !params.get("modalidade")) return { ...EMPTY, ...saved };
    if (tipo === "pf") return { ...EMPTY, requester_type: "pf", modality: "pf", category_slug: params.get("area"), step: params.get("area") ? 2 : 1 };
    if (tipo === "pj") return { ...EMPTY, requester_type: "pj", modality: params.get("modalidade"), step: 1 };
    return saved ? { ...EMPTY, ...saved } : EMPTY;
  });
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(null);

  useEffect(() => { api.get("/public/categories").then((r) => setCats(r.data)).catch(() => {}); }, []);
  useEffect(() => {
    if (!done) saveDraft(d);
    setNext("/solicitar");
  }, [d, done]);
  useEffect(() => {
    if (d.step >= 1 && !d.tracked_start) { api.post("/public/track", { event: "form_start" }).catch(() => {}); setD((x) => ({ ...x, tracked_start: true })); }
    if (d.step === 4 && !d.tracked_review) { api.post("/public/track", { event: "form_review" }).catch(() => {}); setD((x) => ({ ...x, tracked_review: true })); }
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [d.step, d.tracked_start, d.tracked_review]);

  const set = (patch) => setD((x) => ({ ...x, ...patch }));
  const cat = useMemo(() => cats.find((c) => c.slug === d.category_slug), [cats, d.category_slug]);

  const submit = async () => {
    setSending(true);
    try {
      const body = { requester_type: d.requester_type, modality: d.modality, category_slug: d.category_slug, services: d.services, description: d.description,
        answers: d.answers, urgency: d.urgency, deadline_date: d.deadline_date || null, company: d.requester_type === "pj" ? d.company : null, utm: getUtm() };
      const { data } = await api.post("/requests", body);
      clearDraft();
      localStorage.removeItem("azv_post_login");
      setDone(data);
    } catch (e) { toast.error(errMsg(e)); } finally { setSending(false); }
  };

  if (done)
    return (
      <div className="wrap max-w-2xl py-14" data-testid="request-success">
        <CheckCircle2 size={56} className="text-[#1465C8]" />
        <h1 className="mt-4 text-3xl font-extrabold md:text-4xl">Solicitação enviada</h1>
        <p className="mt-3 text-lg">Protocolo: <strong className="font-mono text-[#1465C8]" data-testid="success-protocol">{done.protocol}</strong></p>
        <p className="mt-3 text-[#64748B]">{settings?.texts?.confirmation_message}</p>
        <div className="mt-8 flex flex-col gap-3 sm:flex-row">
          <Link to={`/minhas-solicitacoes/${done.request_id}`} className="btn-primary" data-testid="success-track-btn">Acompanhar solicitação</Link>
          <button className="btn-secondary" onClick={() => { setDone(null); setD(EMPTY); nav("/solicitar"); }} data-testid="success-new-btn">Nova solicitação</button>
        </div>
        <div className="mt-12"><p className="mb-3 text-sm font-semibold text-[#64748B]">Conhece alguém que precisa? Compartilhe a plataforma (seus dados não são compartilhados).</p><ShareBlock compact testid="success-share" /></div>
      </div>
    );

  const steps = STEP_NAMES.map((n, i) => (i === 1 && d.requester_type === "pj" ? "Serviço" : n));
  const titles = ["Para quem é a solicitação?", d.requester_type === "pj" ? "Qual modalidade e serviço?" : "Qual é a área do Direito?", "Descreva sua situação", "Qual é o nível de urgência?", "Revise e envie"];
  return (
    <div className="bg-[#EDF5FF]/50 pb-16">
      <div className="wrap pt-8 md:pt-12">
        <div className="mb-6 flex items-center gap-2 overflow-x-auto scroll-x" data-testid="wizard-progress">
          {steps.map((n, i) => (
            <div key={n} className="flex shrink-0 items-center gap-2">
              <span className={`flex h-8 w-8 items-center justify-center rounded-full text-sm font-bold ${i < d.step ? "bg-[#1465C8] text-white" : i === d.step ? "bg-[#092C66] text-white" : "bg-white text-[#64748B] ring-1 ring-slate-200"}`}>{i + 1}</span>
              <span className={`text-sm font-semibold ${i === d.step ? "text-[#092C66]" : "text-[#64748B]"} ${i === d.step ? "" : "hidden sm:inline"}`}>{n}</span>
              {i < 4 && <span className="h-px w-6 bg-slate-300" />}
            </div>
          ))}
        </div>
        <h1 className="rise text-3xl font-extrabold md:text-4xl" data-testid="wizard-step-title">{titles[d.step]}</h1>
        <div className="mt-6" key={d.step}>
          <div className="rise">
            {d.step === 0 && <StepPerfil d={d} set={set} />}
            {d.step === 1 && <StepArea d={d} set={set} cats={cats} bs={settings?.business_services || {}} />}
            {d.step === 2 && <StepDescricao d={d} set={set} cat={cat} />}
            {d.step === 3 && <StepUrgencia d={d} set={set} />}
            {d.step === 4 && <StepRevisao d={d} set={set} cat={cat} onSubmit={submit} sending={sending} />}
          </div>
        </div>
        <div className="mt-8 flex flex-wrap items-center gap-3">
          {d.step > 0 && <button className="btn-secondary" onClick={() => set({ step: d.step - 1 })} data-testid="wizard-back-button"><ArrowLeft size={18} />Voltar</button>}
          {d.step > 0 && d.step < 4 && <button className="btn-primary" disabled={!canAdvance(d)} onClick={() => set({ step: d.step + 1 })} data-testid="wizard-next-step-button">Continuar<ArrowRight size={18} /></button>}
          {(d.description || d.step > 0) && (
            <button className="btn-ghost ml-auto text-[#64748B]" onClick={() => { clearDraft(); setD(EMPTY); }} data-testid="wizard-discard-btn"><Trash2 size={14} />Descartar rascunho</button>
          )}
        </div>
        <p className="mt-4 text-xs text-[#64748B]">Rascunho salvo apenas neste dispositivo até o envio. Não informe dados sensíveis desnecessários neste primeiro contato.</p>
      </div>
    </div>
  );
}
